/**
 * OpenAsProjectDialog — the picker behind "Open as AI Project".
 *
 * WHY IT EXISTS. That button used to resolve silently, and the same click had
 * four different outcomes: open the project the file lives in, reuse whatever
 * workspace the parent folder already had, wrap the parent into a NEW project,
 * or wrap the drive root. Nothing on screen said which was coming. Reuse is the
 * costliest of the four — the conversation joins that project's history and the
 * assistant's file reach becomes that project's links — so the destination is
 * shown BEFORE navigating, and the user picks it.
 *
 * Two sources, deliberately kept apart:
 *   - `suggest-for-file` lists projects that already reach the file (it lives
 *     in one, or one links an ancestor folder). Access-filtered server-side.
 *   - `find-for-file` still answers the parent-folder wrap: which folder would
 *     be wrapped, and — in an org — whether that lands in the member's own
 *     space or the shared one. Reusing it keeps one implementation of the
 *     placement rule instead of a second copy that can drift.
 *
 * "Create new project" hands off to the shared New Project dialog
 * (`useNewProject`), so audience/placement keep their single source of truth;
 * the folder linking that follows is the existing `/open-folder` behaviour.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { LayoutGrid, Loader2, X, FolderPlus, Folder, UserRound, Building2 } from 'lucide-react'
import { useTheme, getThemeClass, Tooltip } from '@papan/core-ui/ui'
import { useNewProject } from '@papan/files/ui'
import { request } from '@papan/api-client'
import { isRootFolderId } from './driveRoot'

export interface SuggestedProject {
  workspaceFolderId: string
  name: string
  /** `contains` = the file lives inside it; `links` = it links an ancestor. */
  reason: 'contains' | 'links'
  /** Which space the project lives in. Org tenants only — a personal tenant
   *  has one destination, so there is nothing to distinguish. */
  placement?: 'individual' | 'organization'
}

/** The parent-folder wrap candidate, straight from `find-for-file`. */
interface WrapTarget {
  parentFolderId: string | null
  parentFolderName: string | null
  scope?: 'individual' | 'organization'
  /** Read-only on both containers — no workspace can be created anywhere. */
  viewerOnly?: boolean
}

interface Props {
  fileId: string
  fileName: string
  /** Open an existing project's workspace folder. */
  onOpenProject: (workspaceFolderId: string) => void
  /** Wrap this folder as a workspace (the existing `/open-folder` path). */
  onWrapFolder: (parentFolderId: string, parentFolderName: string, scope?: 'individual' | 'organization') => void
  /** Hand off to the shared New Project dialog. */
  onCreateNew: () => void
  onClose: () => void
}

export function OpenAsProjectDialog({ fileId, fileName, onOpenProject, onWrapFolder, onCreateNew, onClose }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // The active tenant's display name, read from the provider that already
  // receives it rather than threaded through four SingleFileView call sites.
  // Undefined while the tenant list loads — hence the generic fallback below.
  const { tenantName } = useNewProject()

  const [projects, setProjects] = useState<SuggestedProject[]>([])
  const [wrap, setWrap] = useState<WrapTarget | null>(null)
  /** `find-for-file` did not answer.
   *
   *  Tracked separately because a null `wrap` cannot distinguish "the request
   *  failed" from "it answered, and nothing is viewer-only" — and the create
   *  row keys off `!wrap?.viewerOnly`, so a 500 or a dropped connection made
   *  that read `false` and re-opened the very dead end this dialog was fixed
   *  to close. Unknown must fail CLOSED here: the cost of hiding the row from
   *  someone who could have used it is one extra click elsewhere, while the
   *  cost of showing it to someone who cannot is a filled-in form and a 403. */
  const [wrapUnknown, setWrapUnknown] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    // Both questions in parallel — neither answer depends on the other, and the
    // dialog cannot render its list until it has both.
    Promise.all([
      request<{ projects: SuggestedProject[] }>('/api/workspaces/suggest-for-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      }).catch(() => ({ projects: [] as SuggestedProject[] })),
      request<WrapTarget & { workspaceFolderId: string | null }>('/api/workspaces/find-for-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      }).catch(() => null),
    ]).then(([s, f]) => {
      if (!live) return
      setProjects(s.projects ?? [])
      setWrap(f)
      // The route always answers with an object, so null is the `.catch` above
      // firing — a failure, not an answer.
      setWrapUnknown(f === null)
      setLoading(false)
    })
    return () => { live = false }
  }, [fileId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // The wrap row is offered only when the resolver says a wrap is possible AND
  // it isn't already one of the suggestions (a parent that IS a project comes
  // back as a suggestion, and would otherwise appear twice).
  const wrapFolderId = wrap?.parentFolderId ?? null
  const showWrap = !!wrapFolderId && !!wrap?.parentFolderName && !wrap?.viewerOnly
    && !projects.some(p => p.workspaceFolderId === wrapFolderId)

  // The drive root's folder is literally named `papan`, which is an internal
  // name no reader recognises. Label it the way every other drive surface
  // does (DriveBreadcrumb, the mobile header): "‹name›'s Drive", falling back
  // to "My Drive" while the tenant name is still loading.
  const wrapLabel = wrapFolderId && isRootFolderId(wrapFolderId)
    ? (tenantName ? `${tenantName}’s Drive` : 'My Drive')
    : (wrap?.parentFolderName ?? '')

  const row = (
    key: string,
    icon: React.ReactNode,
    title: string,
    sub: string,
    onClick: () => void,
    /** Destination glyph, right-aligned — see `destination` below. */
    trailing?: React.ReactNode,
  ) => (
    <button
      key={key}
      onClick={onClick}
      data-testid="open-as-project-row"
      className="flex items-start gap-2.5 w-full text-left py-2 px-2 rounded transition-colors themed-text-secondary hover:themed-text hover:themed-bg-tertiary"
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm truncate themed-text">{title}</span>
        <span className="block text-[11px] themed-text-muted truncate">{sub}</span>
      </span>
      {trailing && <span className="mt-0.5 shrink-0 themed-text-muted">{trailing}</span>}
    </button>
  )

  // WHICH SPACE a project belongs to, as the drive nav's own glyphs: the
  // org-shared container (`Users`) or the member's own space (`UserRound`,
  // literally the nav's "My Space" icon).
  //
  // One meaning for the whole column: every row answers "which space is this
  // project in?" — existing rows from their actual placement, the folder row
  // from where its new project WOULD be created. Mixing "is" and "would be"
  // in one column is what made the first version unreadable.
  //
  // It says WHERE, not who can open it. The audience — a team, a role, named
  // people — is chosen afterwards in the New Project dialog and simply does
  // not exist yet at this point, so a "Shared" badge here would be a claim
  // about a decision nobody has made. Personal tenants get no glyph: one
  // destination means nothing to distinguish.
  const destination = (scope?: 'individual' | 'organization') =>
    scope === 'organization'
      // `Building2` is the drive's OWN organization glyph — the Projects page
      // heads its "From Organization" section with it. The side nav's `Users`
      // (which this used at first) means the member list, not the org space.
      //
      // Tooltips because a bare 14px glyph cannot say which space it means;
      // the label is tense-neutral since the same glyph marks a project that
      // IS in a space and a folder row whose new project WOULD go there.
      // `always` is REQUIRED here: Tooltip's default only fires when the child
      // is truncated, and an icon never is — without it neither would appear.
      //
      // Both tooltips state the LOCATION only. An earlier draft said "only you
      // can see it" of My Space, which is false: a project there can still be
      // shared with named people. That is the same "WHERE, not who" rule as
      // the note above — the glyph column must not make an audience claim.
      ? (
        <Tooltip content="This project lives in the organization’s shared space" placement="top" always>
          <Building2 size={14} aria-label="Organization" />
        </Tooltip>
      )
      : scope === 'individual'
        ? (
          <Tooltip content="This project lives in My Space" placement="top" always>
            <UserRound size={14} aria-label="My Space" />
          </Tooltip>
        )
        : null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div
        data-testid="open-as-project-dialog"
        className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-[420px] max-h-[520px] flex flex-col`}
      >
        {/* `gap-3` so a truncated long filename never runs right up against
            the close button — `justify-between` alone leaves them touching
            once the title grows to fill its half. */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b themed-border">
          {/* The filename is the only part allowed to truncate. Truncating the
              whole sentence ate "in a project", which is the half that says
              what the dialog is FOR.

              The CLOSING quote rides with the TAIL, not with the filename:
              inside the truncating span the ellipsis eats it, so a long name
              rendered an opening quote with no closing one.

              Spacing is explicit &nbsp;, not a flex `gap` — a gap would push
              that closing quote 4px clear of the ellipsis and leave it
              floating in the middle of the line. */}
          <h3 className="flex items-baseline min-w-0 text-sm font-medium themed-text">
            <LayoutGrid size={16} className="themed-text-secondary shrink-0 self-center mr-1.5" />
            <span className="shrink-0">Open&nbsp;“</span>
            <span className="truncate min-w-0">{fileName}</span>
            <span className="shrink-0">”&nbsp;in a project</span>
          </h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text shrink-0" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-2 min-h-[160px]">
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 size={16} className="animate-spin themed-text-muted" />
            </div>
          ) : (
            <>
              {/* The folder row belongs UNDER this heading too: from the
                  reader's side it is another place this file can be opened,
                  not a separate kind of thing. */}
              {(projects.length > 0 || showWrap) && (
                <div className="px-2 pt-1 pb-1.5 text-[11px] uppercase tracking-wider themed-text-muted">
                  Suggested Project
                </div>
              )}
              {projects.map(p => row(
                p.workspaceFolderId,
                <LayoutGrid size={20} className="text-purple-400" />,
                p.name,
                // Vocabulary borrowed from the workspace explorer's own two
                // sections — "In this Project" and "Linked from Drives" — so the
                // words mean the same thing on both surfaces. `link` is used
                // only where there IS a link: a file sitting inside the project
                // folder is not linked to it.
                p.reason === 'contains'
                  ? 'Existing project that contains this file'
                  : 'Existing project that links to this file’s folder',
                () => onOpenProject(p.workspaceFolderId),
                destination(p.placement),
              ))}

              {showWrap && row(
                'wrap',
                // Green like the other CREATE row: this makes a new project too.
                <Folder size={20} className="text-green-400" />,
                wrapLabel,
                // NOT "turn this folder into a project" — nothing happens to the
                // folder. A new project is created that LINKS it, and the folder
                // stays exactly where it is with its contents untouched.
                // The destination now rides the trailing glyph instead of a
                // ", shared with the organization" tail on this sentence.
                'Create a project that links to this folder',
                () => onWrapFolder(wrapFolderId!, wrap!.parentFolderName!, wrap!.scope),
                destination(wrap!.scope),
              )}

              {projects.length === 0 && !showWrap && (
                <div className="text-sm themed-text-muted text-center py-8">
                  {wrapUnknown
                    ? 'No project holds this file, and we couldn’t check whether you can create one. Close this and try again.'
                    : wrap?.viewerOnly
                      ? 'No project holds this file, and you don’t have permission to create one here.'
                      : 'No project holds this file yet.'}
                </div>
              )}

              {/* `viewerOnly` is the server saying the caller can create in
                  NEITHER container. Offering this row anyway walked the user
                  through naming a project and picking an audience only to fail
                  with "Could not create the project" at the POST. The wrap row
                  above is already hidden on the same flag, and `/can-create` —
                  the endpoint that owns this question — answers `false` in
                  exactly the same cases, including the container-not-created-
                  yet one. So this follows the repo's existing answer rather
                  than inventing a second, more permissive one. */}
              {!wrap?.viewerOnly && !wrapUnknown && (
                <div className="mt-1 pt-1 border-t themed-border">
                  {row(
                    'new',
                    <FolderPlus size={20} className="text-green-400" />,
                    'Create a new project…',
                    // Deliberately not "add this file to it": creating a project
                    // leaves the file where it is and opens it as a tab. The
                    // dialog's own title already says the file will open in it.
                    // "who can access it" is the New Project dialog's own
                    // question, so the two surfaces use one phrasing.
                    'Start an empty project and choose who can access it',
                    onCreateNew,
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
